import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Chip, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { PopoverState } from '../../hooks/useItemPopover';

interface ItemDetailsPopoverProps {
  popover: PopoverState | null;
  onClose: () => void;
  showDeleteAction?: boolean;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  showUseAction?: boolean;
  onUse?: () => void;
  useDisabled?: boolean;
  remainingUsesLabel?: string;
}

export const ItemDetailsPopover: React.FC<ItemDetailsPopoverProps> = ({
  popover,
  onClose,
  showDeleteAction = false,
  onDelete,
  deleteDisabled = false,
  showUseAction = false,
  onUse,
  useDisabled = false,
  remainingUsesLabel,
}) => (
  <Popover
    open={Boolean(popover)}
    anchorEl={popover?.anchor}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
  >
    <Box sx={{ p: 2, maxWidth: 300 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {popover?.name}
          {popover?.damageModifier !== null && popover?.damageModifier !== undefined
            ? ` (${popover.damageModifier >= 0 ? '+' : ''}${popover.damageModifier})`
            : ''}
        </Typography>
        {showDeleteAction && onDelete && (
          <Tooltip title="Delete item">
            <span>
              <IconButton 
                aria-label="Delete item" 
                size="small" 
                onClick={onDelete} 
                disabled={deleteDisabled}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {popover?.description && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {popover.description}
        </Typography>
      )}
      {popover?.modifiers && (
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
          >
            Modifiers
          </Typography>
          <Typography variant="body2">{popover.modifiers}</Typography>
        </Box>
      )}
      {(popover?.tags?.length ?? 0) > 0 && (
        <Box>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
          >
            Special Tags
          </Typography>
          {popover!.tags.map((tag) => (
            <Typography key={tag.id} variant="body2">
              • {tag.description}
            </Typography>
          ))}
        </Box>
      )}
      {(popover?.categories?.length ?? 0) > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
          >
            Categories
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {popover!.categories!.map((cat) => (
              <Chip key={cat.id} label={cat.description} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}
      {remainingUsesLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {remainingUsesLabel}
        </Typography>
      )}
      {showUseAction && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            disabled={useDisabled}
            onClick={() => { onUse?.(); }}
            fullWidth
          >
            {useDisabled ? 'No uses remaining' : 'Use'}
          </Button>
        </Box>
      )}
    </Box>
  </Popover>
);
